import React, { useEffect } from "react";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiForm,
  EuiFormRow,
  EuiSelect,
  EuiSpacer,
  EuiTextArea,
  EuiTitle,
} from "@elastic/eui";
import { Contact, LeadPriority, LeadStatus } from "..";
import { useApi } from "src/api";
import { AnketaQuestionnaire } from "src/questionnaires";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useAnketaI18n } from "src/internationalization";

export const UpdateContact = ({
  closeFlyout,
  selectedContact,
  formData,
  setFormData,
  updateContact,
  loading,
}: {
  closeFlyout: () => void;
  selectedContact: Contact;
  formData: Partial<Contact>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<Contact>>>;
  updateContact: () => Promise<void>;
  loading: boolean;
}): JSX.Element => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const fetchQuestionnaire = async () => {
    const questioners = await Get<AnketaQuestionnaire>({
      path: `v2/${AnketaResourcesEnum.opsanio}/questioners`,
      responseType: "json",
      token: keycloak?.token,
    });
    if (questioners.success) {
    }
  };
  const priorityOptions = [
    {
      value: LeadPriority.low,
      text: translate("anketa.leads.priority.low", { defaultMessage: "Low" }),
    },
    {
      value: LeadPriority.medium,
      text: translate("anketa.leads.priority.medium", {
        defaultMessage: "Medium",
      }),
    },
    {
      value: LeadPriority.high,
      text: translate("anketa.leads.priority.high", { defaultMessage: "High" }),
    },
  ];

  const statusOptions = [
    {
      value: LeadStatus.new,
      text: translate("anketa.leads.status.new", { defaultMessage: "New" }),
    },
    {
      value: LeadStatus.in_progress,
      text: translate("anketa.leads.status.in_progress", {
        defaultMessage: "In Progress",
      }),
    },
    {
      value: LeadStatus.resolved,
      text: translate("anketa.opsanio.contact.status.resolved", {
        defaultMessage: "Closed Successfully",
      }),
    },
    {
      value: LeadStatus.closed,
      text: translate("anketa.leads.status.closed", {
        defaultMessage: "Closed",
      }),
    },
  ];

  useEffect(() => {
    fetchQuestionnaire();
  }, []);

  return (
    <EuiForm component="form">
      <EuiFlyout onClose={closeFlyout} size="m">
        <EuiFlyoutHeader hasBorder>
          <EuiTitle size="m">
            <h2>
              {translate("anketa.opsanio.contact.edit.title", {
                values: { name: selectedContact.name },
                defaultMessage: "Edit Contact: {{name}}",
              })}
            </h2>
          </EuiTitle>
        </EuiFlyoutHeader>

        <EuiFlyoutBody>
          <EuiFormRow
            label={translate("anketa.opsanio.contact.field.name", {
              defaultMessage: "Name",
            })}
            fullWidth
          >
            <EuiFieldText
              value={formData.name || ""}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              fullWidth
            />
          </EuiFormRow>
          <EuiFormRow
            label={translate("anketa.opsanio.contact.field.email", {
              defaultMessage: "Email",
            })}
            fullWidth
          >
            <EuiFieldText
              value={formData.email || ""}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              fullWidth
            />
          </EuiFormRow>
          <EuiFormRow
            label={translate("anketa.opsanio.contact.field.company", {
              defaultMessage: "Company",
            })}
            fullWidth
          >
            <EuiFieldText
              value={formData.company || ""}
              onChange={(e) =>
                setFormData({ ...formData, company: e.target.value })
              }
              fullWidth
            />
          </EuiFormRow>
          <EuiFormRow
            label={translate("anketa.opsanio.contact.field.topic", {
              defaultMessage: "Topic",
            })}
            fullWidth
          >
            <EuiFieldText
              value={formData.topic || ""}
              onChange={(e) =>
                setFormData({ ...formData, topic: e.target.value })
              }
              fullWidth
            />
          </EuiFormRow>
          <EuiFormRow
            label={translate("anketa.opsanio.contact.field.message", {
              defaultMessage: "Message",
            })}
            fullWidth
          >
            <EuiTextArea
              value={formData.message || ""}
              onChange={(e) =>
                setFormData({ ...formData, message: e.target.value })
              }
              rows={5}
              fullWidth
            />
          </EuiFormRow>
          <EuiFormRow
            label={translate("anketa.leads.priority", {
              defaultMessage: "Priority",
            })}
            fullWidth
          >
            <EuiSelect
              options={priorityOptions}
              value={formData.priority || LeadPriority.low}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  priority: e.target.value as LeadPriority,
                })
              }
              fullWidth
            />
          </EuiFormRow>
          <EuiFormRow
            label={translate("anketa.opsanio.contact.field.status", {
              defaultMessage: "Status",
            })}
            fullWidth
          >
            <EuiSelect
              options={statusOptions}
              value={formData.status || LeadStatus.new}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as LeadStatus,
                })
              }
              fullWidth
            />
          </EuiFormRow>

          <EuiFormRow
            label={translate("anketa.opsanio.contact.field.status", {
              defaultMessage: "Status",
            })}
            fullWidth
          >
            <EuiSelect
              options={statusOptions}
              value={formData.status || LeadStatus.new}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as LeadStatus,
                })
              }
              fullWidth
            />
          </EuiFormRow>
          <EuiSpacer size="l" />
        </EuiFlyoutBody>
        <EuiFlyoutFooter>
          <EuiFlexGroup justifyContent="flexEnd">
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty onClick={closeFlyout}>
                {translate("anketa.common.cancel", {
                  defaultMessage: "Cancel",
                })}
              </EuiButtonEmpty>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButton onClick={updateContact} fill isLoading={loading}>
                {translate("anketa.opsanio.contact.save", {
                  defaultMessage: "Save Changes",
                })}
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlyoutFooter>
      </EuiFlyout>
    </EuiForm>
  );
};
